import { isInFilter } from "../utils/dateFilter";

export const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "paused", label: "Paused" },
  { id: "completed", label: "Completed" },
  { id: "attention", label: "Needs attention" },
];

export const FREQUENCY_OPTIONS = [
  { value: "", label: "All frequencies" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom", label: "Custom" },
];

export function nextRunDate(tpl) {
  return tpl?.next_run_date || tpl?.upcoming?.date || null;
}

export function needsAttention(tpl) {
  if (!tpl) return false;
  const status = String(tpl.status || "").toLowerCase();
  if (tpl.missed_runs > 0 && status === "active") return true;
  if (tpl.last_skip_reason === "previous_unpaid") return true;
  if (status === "active" && nextRunDate(tpl)) {
    const today = new Date().toISOString().split("T")[0];
    if (String(nextRunDate(tpl)) < today) return true;
  }
  return false;
}

export function toMonthlyAmount(tpl) {
  const amount = parseFloat(tpl?.grand_total) || 0;
  const freq = String(tpl?.frequency || "monthly").toLowerCase();
  switch (freq) {
    case "weekly":
      return amount * 4.33;
    case "biweekly":
      return amount * 2.165;
    case "monthly":
      return amount;
    case "quarterly":
      return amount / 3;
    case "yearly":
      return amount / 12;
    case "custom": {
      const days = parseInt(tpl?.interval_days, 10) || 30;
      return amount * (30 / days);
    }
    default:
      return amount;
  }
}

export function matchesStatusTab(tpl, tabId) {
  if (tabId === "all") return true;
  if (tabId === "attention") return needsAttention(tpl);
  return String(tpl?.status || "").toLowerCase() === tabId;
}

export function matchesSearch(tpl, query) {
  if (!query?.trim()) return true;
  const s = query.toLowerCase();
  const recentNums = (tpl.recent_invoices || [])
    .map((inv) => inv.invoice_number)
    .filter(Boolean)
    .join(" ");
  return (
    String(tpl.template_name || "").toLowerCase().includes(s) ||
    String(tpl.customer_name || "").toLowerCase().includes(s) ||
    String(tpl.last_invoice?.invoice_number || "").toLowerCase().includes(s) ||
    recentNums.toLowerCase().includes(s)
  );
}

export function matchesPeriod(tpl, period) {
  const dateStr = nextRunDate(tpl);
  if (!dateStr || dateStr === "0000-00-00") return period === "All Time";
  return isInFilter(dateStr, period);
}

export function matchesDateRange(tpl, customDate) {
  if (!customDate?.from && !customDate?.to) return true;
  const dateStr = nextRunDate(tpl);
  if (!dateStr || dateStr === "0000-00-00") return false;
  const d = new Date(`${dateStr}T12:00:00`);
  const from = customDate.from ? new Date(`${customDate.from}T00:00:00`) : null;
  const to = customDate.to ? new Date(`${customDate.to}T23:59:59`) : null;
  return (!from || d >= from) && (!to || d <= to);
}

export function sortTemplates(list, sortField, sortOrder) {
  const sorted = [...list];
  sorted.sort((a, b) => {
    let cmp = 0;
    if (sortField === "amount") {
      cmp = (parseFloat(a.grand_total) || 0) - (parseFloat(b.grand_total) || 0);
    } else if (sortField === "customer") {
      cmp = String(a.customer_name || "").localeCompare(String(b.customer_name || ""));
    } else if (sortField === "name") {
      cmp = String(a.template_name || "").localeCompare(String(b.template_name || ""));
    } else {
      const da = nextRunDate(a) || "9999-99-99";
      const db = nextRunDate(b) || "9999-99-99";
      cmp = da.localeCompare(db);
    }
    return sortOrder === "asc" ? cmp : -cmp;
  });
  return sorted;
}

export function uniqueCustomers(templates) {
  const map = new Map();
  (templates || []).forEach((tpl) => {
    const name = tpl.customer_name?.trim();
    if (name && !map.has(name)) map.set(name, name);
  });
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
}

export function computeSummary(templates, period) {
  const inPeriod = (templates || []).filter((tpl) => matchesPeriod(tpl, period));
  const active = inPeriod.filter((tpl) => String(tpl.status) === "active");
  const dueThisWeek = active.filter((tpl) => matchesPeriod(tpl, "Weekly"));
  const attention = inPeriod.filter(needsAttention);

  const monthlyValue = active.reduce((sum, tpl) => sum + toMonthlyAmount(tpl), 0);
  const dueWeekAmount = dueThisWeek.reduce(
    (sum, tpl) => sum + (parseFloat(tpl.grand_total) || 0),
    0
  );

  return {
    activeCount: active.length,
    monthlyValue,
    dueWeekCount: dueThisWeek.length,
    dueWeekAmount,
    attentionCount: attention.length,
  };
}

export function formatInr(amount) {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return "₹0.00";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatScheduleDate(d) {
  if (!d || d === "0000-00-00") return "—";
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

export function isOverdueNextRun(tpl) {
  if (String(tpl?.status) !== "active" || !nextRunDate(tpl)) return false;
  const today = new Date().toISOString().split("T")[0];
  return String(nextRunDate(tpl)) < today;
}

export function getAttentionReasons(tpl) {
  const reasons = [];
  if (tpl.missed_runs > 0 && String(tpl.status) === "active") {
    reasons.push(
      `${tpl.missed_runs} missed billing period${tpl.missed_runs > 1 ? "s" : ""}`
    );
  }
  if (tpl.last_skip_reason === "previous_unpaid") {
    reasons.push("Waiting for last invoice payment");
  }
  if (isOverdueNextRun(tpl)) {
    reasons.push("Next run date is overdue");
  }
  return reasons;
}

function formatYmd(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function parseYmd(ymd) {
  const [year, month, day] = String(ymd).split("-").map(Number);
  return { year, month, day };
}

function addCalendarDays(ymd, days) {
  const { year, month, day } = parseYmd(ymd);
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + days);
  return formatYmd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function addMonthsAnchored(fromYmd, months) {
  const { year, month, day } = parseYmd(fromYmd);
  let targetMonth = month + months;
  let targetYear = year;
  while (targetMonth > 12) {
    targetMonth -= 12;
    targetYear += 1;
  }
  while (targetMonth < 1) {
    targetMonth += 12;
    targetYear -= 1;
  }
  const lastDay = new Date(targetYear, targetMonth, 0).getDate();
  return formatYmd(targetYear, targetMonth, Math.min(day, lastDay));
}

export function computeNextRunDateClient(fromYmd, frequency, intervalDays = null) {
  const base = fromYmd;
  if (!base || !/^\d{4}-\d{2}-\d{2}$/.test(base)) return null;

  switch (String(frequency || "monthly").toLowerCase()) {
    case "weekly":
      return addCalendarDays(base, 7);
    case "biweekly":
      return addCalendarDays(base, 14);
    case "monthly":
      return addMonthsAnchored(base, 1);
    case "quarterly":
      return addMonthsAnchored(base, 3);
    case "yearly": {
      const { year, month, day } = parseYmd(base);
      const targetYear = year + 1;
      const lastDay = new Date(targetYear, month, 0).getDate();
      return formatYmd(targetYear, month, Math.min(day, lastDay));
    }
    case "custom": {
      const days = parseInt(intervalDays, 10);
      const step = Number.isFinite(days) && days >= 2 && days <= 365 ? days : 30;
      return addCalendarDays(base, step);
    }
    default:
      return addMonthsAnchored(base, 1);
  }
}

export function frequencyLabel(tpl) {
  if (tpl?.frequency_label) return tpl.frequency_label;
  const f = String(tpl?.frequency || "monthly").toLowerCase();
  if (f === "custom" && tpl?.interval_days) return `Every ${tpl.interval_days} days`;
  const labels = {
    weekly: "Weekly",
    biweekly: "Every 2 weeks",
    monthly: "Monthly",
    quarterly: "Quarterly",
    yearly: "Yearly",
    custom: "Custom",
  };
  return labels[f] || f;
}

/** Collect next-run dates for a template that fall within [monthStart, monthEnd]. */
export function runsInMonth(tpl, year, month, includeProjected = false) {
  const monthStart = formatYmd(year, month, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const monthEnd = formatYmd(year, month, lastDay);
  const dates = [];
  const status = String(tpl?.status || "").toLowerCase();
  const next = nextRunDate(tpl);

  if (!includeProjected) {
    if (next && next >= monthStart && next <= monthEnd) dates.push(next);
    return dates;
  }

  if (status !== "active" || !next) return dates;

  let cursor = next;
  let guard = 0;
  while (cursor && guard < 48) {
    guard += 1;
    if (tpl.end_date && String(cursor) > String(tpl.end_date)) break;
    if (cursor > monthEnd) break;
    if (cursor >= monthStart && cursor <= monthEnd) {
      dates.push(cursor);
    }
    cursor = computeNextRunDateClient(cursor, tpl.frequency, tpl.interval_days);
    if (!cursor || cursor === dates[dates.length - 1]) break;
  }

  return dates;
}

/** Map YYYY-MM-DD → [{ template, projected }] for calendar month. */
export function buildCalendarMap(templates, year, month, includeProjected = false) {
  const map = {};
  (templates || []).forEach((tpl) => {
    const dates = runsInMonth(tpl, year, month, includeProjected);
    const baseDate = nextRunDate(tpl);
    dates.forEach((date) => {
      if (!map[date]) map[date] = [];
      map[date].push({
        template: tpl,
        projected: includeProjected && date !== baseDate,
      });
    });
  });
  return map;
}

export function matchesCalendarDate(tpl, dateYmd, includeProjected = false) {
  if (!dateYmd) return true;
  const [year, month] = dateYmd.split("-").map(Number);
  const dates = runsInMonth(tpl, year, month, includeProjected);
  return dates.includes(dateYmd);
}

export function exportRecurringCsv(rows, filename = "auto-billing-schedules.csv") {
  const headers = [
    "Schedule",
    "Customer",
    "Status",
    "Frequency",
    "Amount",
    "Next run",
    "End date",
    "Runs",
    "Last invoice",
    "Missed runs",
  ];

  const escape = (value) => {
    const str = String(value ?? "");
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };

  const lines = [
    headers.join(","),
    ...(rows || []).map((tpl) =>
      [
        tpl.template_name,
        tpl.customer_name,
        tpl.last_skip_reason === "previous_unpaid" ? "Waiting for payment" : tpl.status,
        frequencyLabel(tpl),
        parseFloat(tpl.grand_total) || 0,
        tpl.next_run_date || "",
        tpl.end_date || "",
        tpl.runs_count || 0,
        tpl.last_invoice?.invoice_number || "",
        tpl.missed_runs || 0,
      ]
        .map(escape)
        .join(",")
    ),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}
